import React, { useState } from 'react';
import SocialMediaForecast from '../../../components/SocialMediaForecast';
import '../../../Assets/Stylesheets/Pages/MarketingServices/Socials.scss';
import API_BASE_URL from '../../../config/api';
import { authHeaders } from '../../../utils/apiAuth';

// Import all the platform logos
import youtubeLogo from '../../../Assets/Images/Youtube_logo.png';
import facebookLogo from '../../../Assets/Images/Facebook_logo.png';
import instagramLogo from '../../../Assets/Images/Instagram_logo.png';
import tiktokLogo from '../../../Assets/Images/Tiktok_logo.png';
import xLogo from '../../../Assets/Images/X_logo.png';
import spotifyLogo from '../../../Assets/Images/Spotify_logo.png';
import snapchatLogo from '../../../Assets/Images/Snapchat_logo.png';
import pinterestLogo from '../../../Assets/Images/Pinterest_logo.png';
import linkedinLogo from '../../../Assets/Images/Linkedin_logo.png';


const MIN_BUDGET = 500;
const MAX_BUDGET = 25000;

export default function OptionAISocials() {
  const [budgetType, setBudgetType] = useState('AI');
  const [aiInput, setAiInput] = useState('');
  const [chosenBudget, setChosenBudget] = useState('');
  const [platformRecommendations, setPlatformRecommendations] = useState([]);
  const [totalBudget, setTotalBudget] = useState(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [explanation, setExplanation] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [forecastData, setForecastData] = useState(null);
  const [showForecast, setShowForecast] = useState(false);
  const [isForecastLoading, setIsForecastLoading] = useState(false);

  const formatCurrency = (value) => {
    return `$${parseFloat(value).toLocaleString()}`;
  };

  const handleBudgetTypeChange = (type) => {
    setBudgetType(type);
    setPlatformRecommendations([]);
    setTotalBudget(0);
    setError('');
  };

  const handleAiInputChange = (event) => {
    setAiInput(event.target.value);
  };

  const handleBudgetChange = (event) => {
    const value = event.target.value;
    if (value === '' || !isNaN(value)) {
    setChosenBudget(value);
      const numValue = parseFloat(value);
      if (!isNaN(numValue) && numValue >= MIN_BUDGET && numValue <= MAX_BUDGET) {
        setError('');
      } else if (numValue > 0 && numValue < MIN_BUDGET) {
        setError(`Minimum budget is ${formatCurrency(MIN_BUDGET)}`);
      } else if (numValue > MAX_BUDGET) {
        setError(`Maximum budget is ${formatCurrency(MAX_BUDGET)}`);
    } else {
      setError('');
    }
    }
  };

  const getAiRecommendations = async () => {
    if (!aiInput.trim()) {
      setError('Please describe your business first');
      return;
    }

    if (budgetType === 'Chose' && (!chosenBudget || parseFloat(chosenBudget) < MIN_BUDGET)) {
      setError(`Please enter a budget of at least ${formatCurrency(MIN_BUDGET)}`);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const budget = budgetType === 'Chose' ? parseFloat(chosenBudget) : null;
      
      console.log("🤖 Requesting AI social media recommendations...");
      console.log("Business description:", aiInput);
      console.log("Budget:", budget || "AI to determine");

      const response = await fetch(`${API_BASE_URL}/api/get-social-recommendations`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          aiInput: aiInput,
          budget: budget
        }),
      });

      if (!response.ok) {
        const responseText = await response.text();
        let errorMessage = `Server error: ${response.status}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          console.error("Non-JSON error response:", responseText.substring(0, 200));
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      console.log("✅ AI Social Recommendations:");
      console.log(`💰 Total Budget: ${formatCurrency(data.totalBudget)}`);
      
      // Store explanation
      if (data.explanation) {
        setExplanation(data.explanation);
        console.log("📝 Explanation received");
      }

      // If we got recommendations from OpenAI, use them
      if (data.recommendations && data.recommendations.length > 0) {
        // Map platform names to logos
        const platformLogosMap = {
          YouTube: youtubeLogo,
          Facebook: facebookLogo,
          Instagram: instagramLogo,
          TikTok: tiktokLogo,
          X: xLogo,
          Spotify: spotifyLogo,
          SnapChat: snapchatLogo,
          Pinterest: pinterestLogo,
          LinkedIn: linkedinLogo,
        };

        const recommendationsWithLogos = data.recommendations.map(rec => ({
          ...rec,
          logo: platformLogosMap[rec.platform] || instagramLogo
        }));

        console.log("📊 Platforms recommended:", recommendationsWithLogos.map(r => r.platform).join(', '));
        
        setPlatformRecommendations(recommendationsWithLogos);
        setTotalBudget(data.totalBudget);
      } else {
        // Do not fabricate recommendations. If the AI returned nothing, show an
        // honest empty state and let the user retry rather than presenting
        // invented platforms/metrics as real analysis.
        console.log("⚠️ No recommendations returned by AI");
        setPlatformRecommendations([]);
        setError('We could not generate recommendations right now. Please try again shortly.');
      }

    } catch (err) {
      console.error('❌ Error getting AI recommendations:', err);
      setError(err.message || 'Failed to get AI recommendations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };


  const getForecast = async () => {
    // If forecast is already showing, just toggle it off
    if (showForecast && forecastData) {
      setShowForecast(!showForecast);
      return;
    }
    
    // If we have cached forecast data, just show it
    if (forecastData) {
      setShowForecast(true);
      return;
    }
    
    if (isForecastLoading) return;
    
    setIsForecastLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/get-social-forecast`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          businessDescription: aiInput,
          totalBudget: totalBudget,
          platformRecommendations: platformRecommendations
        }),
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();
      setForecastData(data);
      setShowForecast(true);
      console.log('📊 Social media forecast data received');
    } catch (error) {
      console.error('❌ Social Forecast Error:', error.message);
      setError('Failed to generate social media forecast. Please try again.');
    } finally {
      setIsForecastLoading(false);
    }
  };

  return (
    <div className="soas-options-container">
      {/* Budget Type Selection */}
      <div className="soas-budget-type-section">
        <h3 className="soas-section-subtitle">Budget Type</h3>
        <div className="soas-budget-type-choice">
          <label className={`soas-radio-option ${budgetType === 'AI' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="budgetType"
            value="AI"
            checked={budgetType === 'AI'}
              onChange={(e) => handleBudgetTypeChange(e.target.value)}
          />
            <span className="soas-radio-label">AI Recommended Budget</span>
        </label>
          <label className={`soas-radio-option ${budgetType === 'Chose' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="budgetType"
            value="Chose"
            checked={budgetType === 'Chose'}
              onChange={(e) => handleBudgetTypeChange(e.target.value)}
          />
            <span className="soas-radio-label">Choose Budget</span>
        </label>
        </div>
      </div>
  
      {/* Manual Budget Input (for "Chose Budget" option) */}
      {budgetType === 'Chose' && (
        <div className="soas-chosen-budget-container">
          <label htmlFor="chosenBudget" className="soas-budget-label">Enter Your Budget</label>
          <input
            type="number"
            id="chosenBudget"
            value={chosenBudget}
            onChange={handleBudgetChange}
            className="soas-budget-input"
            placeholder={`${formatCurrency(MIN_BUDGET)} minimum`}
            min={MIN_BUDGET}
            max={MAX_BUDGET}
          />
        </div>
      )}
  
      {/* Chat Interface for AI Input */}
      <div className="soas-chat-interface">
        <div className="soas-chat-input-container">
        <textarea
          id="aiInput"
          value={aiInput}
          onChange={handleAiInputChange}
            className="soas-chat-input"
            placeholder="Describe your business, target audience, and growth goals... For better accuracy, include your website URL."
          />
          <div className="soas-input-controls">
            <div className="soas-control-left"></div>
            <button 
              className="soas-submit-button" 
              onClick={getAiRecommendations}
              disabled={isLoading || (budgetType === 'Chose' && chosenBudget < MIN_BUDGET)}
            >
              {isLoading ? '...' : '→'}
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && <div className="soas-error-message">{error}</div>}

      {/* AI Recommendations */}
      {platformRecommendations.length > 0 && (
        <div className="soas-recommendations-section">
          <h3 className="soas-section-title">🎯 Recommended Strategy</h3>
          
          <div className="soas-platform-cards">
            {platformRecommendations.map((rec, index) => (
              <div key={index} className="soas-platform-card">
                <div className="soas-platform-header">
                  <img src={rec.logo} alt={rec.platform} className="soas-platform-logo" />
                  <div className="soas-platform-info">
                    <h4>{rec.platform}</h4>
                    <span className={`soas-quality-badge ${rec.qualityTier.toLowerCase()}`}>
                      {rec.qualityTier}
                    </span>
                  </div>
                  <div className="soas-platform-budget">
                    {formatCurrency(rec.budgetAllocation)}
                  </div>
      </div>

                <div className="soas-metrics-grid">
                  {rec.metrics.map((metric, idx) => (
                    <div key={idx} className="soas-metric-item">
                      <span className="soas-metric-name">{metric.name}</span>
                      <span className="soas-metric-value">{metric.quantity}</span>
                      <span className={`soas-priority-badge ${metric.priority.toLowerCase()}`}>
                        {metric.priority}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="soas-strategy">
                  <p>{rec.strategy}</p>
                  <span className="soas-growth-pace">Growth: {rec.growthPace}</span>
                </div>
              </div>
            ))}
        </div>

          {/* See Details and Forecast Buttons */}
          {explanation && (
            <div className="soas-details-section">
              <div className="soas-action-buttons">
                <button 
                  className="soas-see-details-btn"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails ? '▲ Hide Details' : '▼ See Details'}
                </button>
                
                <button 
                  className="soas-see-forecast-btn"
                  onClick={getForecast}
                  disabled={isForecastLoading}
                >
                  {isForecastLoading ? '⏳ Loading...' : showForecast ? '▲ Hide Forecast' : '📈 See Forecast'}
                </button>
              </div>
              
              {showDetails && (
                <div className="soas-explanation-container">
                  <div className="soas-explanation-block">
                    <h4 className="soas-explanation-title">💰 Budget Rationale</h4>
                    <p className="soas-explanation-text">
                      {explanation.budget_rationale || 'Budget optimized for your social media growth goals.'}
                    </p>
                  </div>
                  
                  <div className="soas-explanation-block">
                    <h4 className="soas-explanation-title">📊 Platform Strategy</h4>
                    <p className="soas-explanation-text">
                      {explanation.platform_reasoning || 'Platforms selected based on your target audience and content type.'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Forecast Display */}
          {showForecast && forecastData && (
            <SocialMediaForecast forecastData={forecastData} />
          )}

          {/* Budget Summary */}
          <div className="soas-budget-summary-container">
            <div className="total-budget-container">
              <span className="budget-label">Social Media Growth Budget:</span>
              <span className="budget-value">{formatCurrency(totalBudget)}</span>
            </div>
            <div className="pads-action-buttons-container">
              <button className="pads-add-to-cart">Add to Cart</button>
              <button className="pads-buy-now">Buy Now</button>
            </div>
      </div>
        </div>
      )}
    </div>
  );
}
